import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Encounter } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Paper, Stack, Title } from "@mantine/core";

import { fetchEncounterQuestionnaireResponseVersionHistoryForDiff } from "@/encounters/services/questionnaire-response";
import { CurrentAndPreviousQuestionnaireResponses } from "@/encounters/types/procedures";

import EncounterProceduresDiffCard from "./components/EncounterProceduresDiffCard";
import { fetchEncounterCPTCodeQuestionnaireResponseVersions } from "./services/questionnaire-response";
import { resolveProcedureCodeVersionPair } from "./utils/procedure-code-version-pair";

type EncounterProceduresDiffProps = {
  encounter: Encounter;
};

const EncounterProceduresDiff = ({ encounter }: EncounterProceduresDiffProps) => {
  const medplum = useMedplum();
  const { clientId, versionId } = useParams() as { clientId: string; versionId: string };

  const [currentAndPreviousQuestionnaireResponses, setCurrentAndPreviousQuestionnaireResponses] =
    useState<CurrentAndPreviousQuestionnaireResponses[]>([]);

  useEffect(() => {
    const getQuestionnaireResponseVersions = async () => {
      const [questionnaireResponsesVersions, encounterVersionHistory] = await Promise.all([
        fetchEncounterCPTCodeQuestionnaireResponseVersions(medplum, encounter.id!, clientId),
        fetchEncounterQuestionnaireResponseVersionHistoryForDiff(medplum, encounter),
      ]);
      const encounterVersions = encounterVersionHistory ?? [];

      const currentAndPreviousQuestionnaireResponses = questionnaireResponsesVersions.reduce<
        CurrentAndPreviousQuestionnaireResponses[]
      >((versionPairs, procedureCodeVersions) => {
        const versionPair = resolveProcedureCodeVersionPair(
          procedureCodeVersions,
          versionId,
          encounterVersions,
        );
        if (versionPair.currentQuestionnaireResponse || versionPair.previousQuestionnaireResponse) {
          versionPairs.push(versionPair);
        }
        return versionPairs;
      }, []);

      setCurrentAndPreviousQuestionnaireResponses(currentAndPreviousQuestionnaireResponses);
    };
    getQuestionnaireResponseVersions();
  }, [medplum, encounter, clientId, versionId]);

  if (currentAndPreviousQuestionnaireResponses.length === 0) return null;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Procedures</Title>
      </Stack>
      <Stack>
        {currentAndPreviousQuestionnaireResponses.map((currentAndPreviousQuestionnaireResponse) => {
          const procedureCodeVersion =
            currentAndPreviousQuestionnaireResponse.currentQuestionnaireResponse ??
            currentAndPreviousQuestionnaireResponse.previousQuestionnaireResponse;
          return (
            <EncounterProceduresDiffCard
              key={procedureCodeVersion?.id}
              currentAndPreviousQuestionnaireResponses={currentAndPreviousQuestionnaireResponse}
            />
          );
        })}
      </Stack>
    </Paper>
  );
};

export default EncounterProceduresDiff;
